import { Button, Grid } from '@mui/material';
import { FixedButton } from '@/components/common/button/FixedButton';
import { MobileBar } from '@/components/home/MobileBar';
import { Theme } from '@mui/material/styles';
import React, { FC, ReactElement } from 'react';

interface Props {
  label: string;
  isMobile?: boolean;
  onClick: () => void;
}

/**
 * Bottom inset a dialog's content needs so its last control clears the action area beneath it:
 * the height of the pinned `MobileBar` on a phone,
 * and room under the centred single action of a terminal screen on a desktop.
 * Lives with the action it compensates for so the two cannot drift apart.
 *
 * @param theme          The active theme.
 * @param isMobile       Whether the action is pinned to the bottom edge of the viewport.
 * @param isSingleAction Whether this screen ends the flow and offers one action rather than a pair.
 */
export const getDialogContentPaddingBottom = (
  theme: Theme,
  isMobile?: boolean,
  isSingleAction?: boolean,
): string | undefined => {
  if (isMobile) {
    return theme.spacing(10);
  }
  if (isSingleAction) {
    return theme.spacing(5);
  }
  return undefined;
};

/**
 * The single action a terminal dialog screen offers:
 * pinned to the bottom edge on a phone, half the row and centred on a desktop dialog.
 * Shared so every such screen places it identically.
 *
 * @param props The label, the viewport and the handler.
 */
export const DialogSingleAction: FC<Props> = (props: Props): ReactElement => {
  const { label, isMobile, onClick } = props;

  if (isMobile) {
    return (
      <MobileBar>
        <FixedButton color={'primary'} variant={'contained'} onClick={onClick} data-testid={'dialog-action'}>
          {label}
        </FixedButton>
      </MobileBar>
    );
  }

  return (
    <Grid container={true} justifyContent={'center'}>
      <Grid item={true} xs={6}>
        <Button fullWidth={true} variant={'contained'} onClick={onClick} data-testid={'dialog-action'}>
          {label}
        </Button>
      </Grid>
    </Grid>
  );
};
